/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * ViewModel-layer surface for navigation primitives the view layer needs.
 *
 * Views must not import from `src/model/**` directly (MVVM: model → viewModel
 * → views, enforced by scripts/lint-mvvm.cjs), so everything a screen or a
 * ScreenController subclass under src/views/ needs is re-exported here:
 *
 *   LocationUrls         — route constants for useNavigationService().navigateTo
 *   ScreenController     — abstract base class every screen controller extends
 *   ScreenControllerTypes — the screen-name constants those controllers register under
 *   ModalController      — abstract base class every modal controller extends
 *   InputAction          — the abstract input enum those controllers key off
 *   TInputEvent          — the event a registered handler receives
 *
 * `InputAction` physically lives in `src/model/enums`, not `src/model/navigation`,
 * but it is only ever consumed alongside ScreenController, so it is surfaced
 * here rather than in a viewModel/enums barrel of one.
 */
export {
  LocationUrls,
  SCREEN_DISPLAY_NAMES,
  ScreenController,
  ScreenControllerTypes,
  ModalController,
  NavigationEngine
} from "../../model/navigation";

/**
 * Interceptor factories a view-layer hook installs on the engine. Only the
 * code-matching ones are surfaced: the rest are wired once by
 * NavigationProvider, which predates this barrel and is baselined in
 * scripts/mvvm-baseline.json.
 */
export {
  createDirectionalCodeInterceptor,
  createSecretCodeInterceptor
} from "../../model/navigation";
export type { InputInterceptor } from "../../model/navigation";
export { isPlayerHostingUrl, urlToScreenName } from "../../model/navigation";
export { InputAction } from "../../model/enums/InputAction";

/**
 * Whether browser history has an entry behind the current one, for view-layer
 * hooks that decide between `navigate(-1)` and a fallback or back-stop.
 */
export { canNavigateBackInHistory } from "../../model/utility/canNavigateBackInHistory";

/**
 * Grid focus arithmetic. A controller for a grid screen translates the D-pad
 * action it was handed into a direction its slice understands, so the mapper
 * and its direction type cross the boundary together.
 */
export { gridDirectionFromInputAction } from "../../model/navigation";
export type { TGridDirection, TInputEvent } from "../../model/navigation";

export default {};
